import { useCallback, useEffect, useMemo, useState } from 'react';
import AssigneeCell from './AssigneeCell';
import { tx } from '../../../lib/adminI18n';
import {
  TaskRow, PropertyOpt, CleanerOpt, STATUS_LABEL, jstToday, addDays, mondayOf, monthFirst,
  dateLabel, jstTime, plannedRange, propertyLabel, getJson,
} from './types';

// 清掃業務一覧（mode='open'）／清掃完了一覧（mode='done'）  手順5
type Period = 'this_week' | 'next_week' | 'last_week' | 'this_month' | 'last_month' | 'custom';
const PERIOD_LABEL: Record<Period, string> = {
  get this_week() { return tx('clnT.0'); }, get next_week() { return tx('clnT.1'); }, get last_week() { return tx('clnT.2'); }, get this_month() { return tx('clnT.3'); }, get last_month() { return tx('clnT.4'); }, get custom() { return tx('clnT.5'); },
};

function rangeOf(p: Period, cf: string, ct: string): { from: string; to: string } {
  const today = jstToday();
  const mon = mondayOf(today);
  switch (p) {
    case 'this_week': return { from: mon, to: addDays(mon, 6) };
    case 'next_week': return { from: addDays(mon, 7), to: addDays(mon, 13) };
    case 'last_week': return { from: addDays(mon, -7), to: addDays(mon, -1) };
    case 'this_month': return { from: monthFirst(today), to: addDays(monthFirst(today, 1), -1) };
    case 'last_month': return { from: monthFirst(today, -1), to: addDays(monthFirst(today), -1) };
    default: return { from: cf, to: ct };
  }
}

export interface ListPreset { cleaner?: string; period?: Period; }

export default function CleaningTaskList({ mode, properties, cleaners, onOpenDate, preset }: {
  mode: 'open' | 'done'; properties: PropertyOpt[]; cleaners: CleanerOpt[];
  onOpenDate: (ymd: string) => void; preset?: ListPreset;
}) {
  const isDone = mode === 'done';
  const [period, setPeriod] = useState<Period>(preset?.period || (isDone ? 'this_month' : 'this_week'));
  const [cf, setCf] = useState(jstToday());
  const [ct, setCt] = useState(addDays(jstToday(), 13));
  const [propertyId, setPropertyId] = useState('');
  const [cleaner, setCleaner] = useState(preset?.cleaner || '');   // '' | 'unassigned' | id
  const [status, setStatus] = useState('');
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [limited, setLimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [detail, setDetail] = useState<TaskRow | null>(null);

  const { from, to } = rangeOf(period, cf, ct);
  const statusParam = status || (isDone ? 'completed,needs_review' : 'pending,in_progress,needs_review');

  const load = useCallback(async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || from > to) {
      setMsg(tx('clnT.6')); setTasks([]); setLoading(false); return;
    }
    setLoading(true); setMsg('');
    const q = new URLSearchParams({ from, to, status: statusParam });
    if (propertyId) q.set('property_id', propertyId);
    if (cleaner === 'unassigned') q.set('unassigned', '1');
    else if (cleaner) q.set('cleaner_id', cleaner);
    if (isDone) q.set('order', 'desc');
    try {
      const d = await getJson(`/api/admin/cleaning?${q.toString()}`);
      setTasks(d.tasks || []);
      setLimited(!!d.limited);
    } catch (e: any) { setMsg(e.message); setTasks([]); }
    finally { setLoading(false); }
  }, [from, to, statusParam, propertyId, cleaner, isDone]);
  useEffect(() => { void load(); }, [load]);

  const totalCost = useMemo(() => tasks.reduce((n, t) => n + (Number(t.supply_cost) || 0), 0), [tasks]);



  return (
    <div className="ctl">
      <div className="ctl-filters">
        <div className="ctl-periods" role="group" aria-label={tx('clnT.7')}>
          {(Object.keys(PERIOD_LABEL) as Period[]).map((p) => (
            <button key={p} type="button" className={`ctl-chip ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{PERIOD_LABEL[p]}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="ctl-custom">
            <input type="date" className="clm-input" value={cf} onChange={(e) => setCf(e.target.value)} aria-label={tx('clnT.8')} />
            〜
            <input type="date" className="clm-input" value={ct} onChange={(e) => setCt(e.target.value)} aria-label={tx('clnT.10')} />
          </div>
        )}
        <select className="clm-select" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} aria-label={tx('clnT.11')}>
          <option value="">{tx('clnT.12')}</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="clm-select" value={cleaner} onChange={(e) => setCleaner(e.target.value)} aria-label={tx('clnT.13')}>
          <option value="">{tx('clnT.14')}</option>
          <option value="unassigned">{tx('clnT.15')}</option>
          {cleaners.map((c) => <option key={c.id} value={String(c.id)}>{c.name}{c.is_active ? '' : tx('clnT.16')}</option>)}
        </select>
        <select className="clm-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={tx('clnT.17')}>
          {isDone ? (<>
            <option value="">{tx('clnT.18')}</option>
            <option value="completed">{tx('clnT.19')}</option>
            <option value="needs_review">{tx('clnT.20')}</option>
          </>) : (<>
            <option value="">{tx('clnT.21')}</option>
            <option value="pending">{tx('clnT.22')}</option>
            <option value="in_progress">{tx('clnT.23')}</option>
            <option value="needs_review">{tx('clnT.24')}</option>
          </>)}
        </select>
      </div>
      <div className="ctl-summary">
        {dateLabel(from)}〜{dateLabel(to)}{tx('clnT.25')}<strong>{tasks.length}{tx('clnT.26')}</strong>
        {isDone && totalCost > 0 && <> {tx('clnT.27')} <strong>{totalCost.toLocaleString()}{tx('clnT.28')}</strong></>}
        {limited && <span className="ctl-limited">{tx('clnT.29')}</span>}
      </div>
      {msg && <p className="cln-msg">{msg}</p>}

      {loading ? <p>{tx('clnT.30')}</p> : tasks.length === 0 ? (
        <p className="cln-detail-empty">{tx('clnT.31')}</p>
      ) : isDone ? (
        <table className="ctl-table">
          <thead>
            <tr><th>{tx('clnT.32')}</th><th>{tx('clnT.11')}</th><th>{tx('clnT.13')}</th><th>{tx('clnT.33')}</th><th>{tx('clnT.34')}</th><th>{tx('clnT.35')}</th><th>{tx('clnT.36')}</th><th>{tx('clnT.37')}</th><th></th></tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} className={`ctl-row ${t.status}`}>
                <td data-label={tx('clnT.32')}>{dateLabel(t.cleaning_date)}</td>
                <td data-label={tx('clnT.11')}>{propertyLabel(t)}{t.photo_count ? <span className="ctl-photo-badge" title={tx('clnT.89', { p0: t.photo_count })} style={{ marginLeft: 6, fontSize: '.78rem', whiteSpace: 'nowrap', background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px' }}>📷 {t.photo_count}</span> : null}</td>
                <td data-label={tx('clnT.13')}><AssigneeCell t={t} /></td>
                <td data-label={tx('clnT.33')}>{jstTime(t.actual_start_at, t.cleaning_date) || '—'}</td>
                <td data-label={tx('clnT.34')}>{jstTime(t.actual_end_at, t.cleaning_date) || '—'}</td>
                <td data-label={tx('clnT.35')}><span className={`cln-status cln-status-${t.status}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
                <td data-label={tx('clnT.36')}>{t.supply_cost ? tx('clnT.38', { p0: Number(t.supply_cost).toLocaleString() }) : '—'}</td>
                <td data-label={tx('clnT.39')}><ReviewBadge t={t} /></td>
                <td><button className={`cln-btn ${t.status === 'completed' && !t.review_status ? 'cln-btn-primary' : 'cln-btn-ghost'}`} onClick={() => setDetail(t)}>
                  {t.status === 'completed' && !t.review_status ? tx('clnT.40') : tx('clnT.41')}
                </button></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="ctl-table">
          <thead>
            <tr><th>{tx('clnT.32')}</th><th>{tx('clnT.11')}</th><th>{tx('clnT.13')}</th><th>{tx('clnT.42')}</th><th>{tx('clnT.17')}</th></tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} className={`ctl-row clickable ${t.status}`} onClick={() => onOpenDate(t.cleaning_date)}>
                <td data-label={tx('clnT.32')}>{dateLabel(t.cleaning_date)}</td>
                <td data-label={tx('clnT.11')}>{propertyLabel(t)}{t.photo_count ? <span className="ctl-photo-badge" title={tx('clnT.89', { p0: t.photo_count })} style={{ marginLeft: 6, fontSize: '.78rem', whiteSpace: 'nowrap', background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '1px 7px' }}>📷 {t.photo_count}</span> : null}</td>
                <td data-label={tx('clnT.13')}><AssigneeCell t={t} /></td>
                <td data-label={tx('clnT.42')}>{plannedRange(t)}</td>
                <td data-label={tx('clnT.17')}><span className={`cln-status cln-status-${t.status}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!isDone && tasks.length > 0 && <p className="clm-note">{tx('clnT.43')}</p>}

      {detail && (
        <ReviewModal task={detail} onClose={() => setDetail(null)} onOpenDate={(d) => { setDetail(null); onOpenDate(d); }}
          onDone={async (m) => { setDetail(null); setMsg(m); await load(); }} />
      )}
    </div>
  );
}

// 承認状態のバッジ
function ReviewBadge({ t }: { t: TaskRow }) {
  if (t.review_status === 'approved') return <span className="ctl-checked">{tx('clnT.44')}</span>;
  if (t.review_status === 'rejected' && t.status !== 'completed') return <span className="ctl-rejected">{tx('clnT.45')}</span>;
  if (t.status === 'completed') return <span className="ctl-pending">{tx('clnT.46')}</span>;
  return <span className="ctl-unchecked">—</span>;
}

interface PhotoRow { id: number; category: string; caption: string | null; created_at: string; uploader_name: string | null; }
const PHOTO_CAT_LABEL: Record<string, string> = { get before() { return tx('clnT.84'); }, get after() { return tx('clnT.85'); }, get lost() { return tx('clnT.86'); }, get damage() { return tx('clnT.87'); }, get other() { return tx('clnT.88'); } };
interface CommentRow { id: number; kind: string; body: string | null; author_type: string; author_name: string | null; created_at: string; }
const KIND_LABEL: Record<string, string> = { get approve() { return tx('clnT.47'); }, get reject() { return tx('clnT.48'); }, get comment() { return tx('clnT.49'); } };

// 清掃結果の確認（承認・却下・コメント）
function ReviewModal({ task, onClose, onDone }: {
  task: TaskRow; onClose: () => void; onDone: (msg: string) => void; onOpenDate: (ymd: string) => void;
}) {
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  // 清掃者が完了報告でチェックした項目（2026-10-31）
  const [checklist, setChecklist] = useState<{ item_id: number | null; label: string; checked_at: string; checked_by_name: string | null }[] | null>(null);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const canReview = task.status === 'completed';

  useEffect(() => {
    getJson(`/api/admin/cleaning/${task.id}/comments`).then((d) => setComments(d.comments || [])).catch(() => {});
    getJson(`/api/admin/cleaning/${task.id}/photos`).then((d) => setPhotos(d.photos || [])).catch(() => {});
    getJson(`/api/admin/cleaning/${task.id}/checklist`).then((d) => setChecklist(d.checklist || [])).catch(() => setChecklist([]));
  }, [task.id]);

  async function send(action: 'approve' | 'reject' | 'comment') {
    setErr('');
    if ((action === 'reject' || action === 'comment') && !text.trim()) {
      setErr(action === 'reject' ? tx('clnT.50') : tx('clnT.51'));
      return;
    }
    setBusy(true);
    try {
      const d = await getJson(`/api/admin/cleaning/${task.id}/review`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, comment: text.trim() }),
      });
      const who = d.notified ? tx('clnT.52', { p0: d.notified }) : tx('clnT.53');
      onDone(action === 'approve' ? tx('clnT.54', { p0: who }) : action === 'reject' ? tx('clnT.55', { p0: who }) : tx('clnT.56', { p0: who }));
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); setConfirmReject(false); }
  }

  return (
    <div className="cln-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="cln-modal crv" onClick={(e) => e.stopPropagation()}>
        <div className="crv-head">
          <h3>{tx('clnT.57')}</h3>
          <ReviewBadge t={task} />
        </div>
        <dl className="ctl-dl">
          <dt>{tx('clnT.11')}</dt><dd>{propertyLabel(task)}</dd>
          <dt>{tx('clnT.32')}</dt><dd>{dateLabel(task.cleaning_date)}</dd>
          <dt>{tx('clnT.13')}</dt><dd><AssigneeCell t={task} /></dd>
          <dt>{tx('clnT.58')}</dt><dd>{jstTime(task.actual_start_at, task.cleaning_date) || '—'} 〜 {jstTime(task.actual_end_at, task.cleaning_date) || '—'}</dd>
          <dt>{tx('clnT.35')}</dt><dd>{STATUS_LABEL[task.status] || task.status}</dd>
          {task.issue_reason && (<><dt>{tx('clnT.59')}</dt><dd className="ctl-pre">{task.issue_reason}</dd></>)}
          <dt>{tx('clnT.60')}</dt><dd className="ctl-pre">{task.report_note || '—'}</dd>
          <dt>{tx('clnT.61')}</dt><dd>{task.supply_cost ? tx('clnT.38', { p0: Number(task.supply_cost).toLocaleString() }) : '—'}</dd>
          {task.note && (<><dt>{tx('clnT.62')}</dt><dd className="ctl-pre">{task.note}</dd></>)}
        </dl>

        {checklist && (checklist.length > 0 || task.status === 'completed') && (
          <div className="crv-history">
            <div className="crv-history-title">{tx('clnCk.result', { n: checklist.length })}</div>
            {checklist.length === 0 ? (
              <p className="clm-note">{tx('clnCk.noResult')}</p>
            ) : (<>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
                {checklist.map((c, i) => (
                  <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: '.9rem' }}>
                    <span aria-hidden>✅</span><span style={{ flex: 1 }}>{c.label}</span>
                  </li>
                ))}
              </ul>
              <p className="clm-note" style={{ marginTop: 6 }}>
                {tx('clnCk.checkedBy', { s: checklist[0].checked_by_name || '—', t: `${checklist[0].checked_at ? dateLabel(new Date(new Date(checklist[0].checked_at).getTime() + 9 * 3600e3).toISOString().slice(0, 10)) : ''} ${jstTime(checklist[0].checked_at)}` })}
              </p>
            </>)}
          </div>
        )}

        {photos.length > 0 && (
          <div className="crv-history">
            <div className="crv-history-title">{tx('clnT.83', { p0: photos.length })}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
              {photos.map((p) => (
                <figure key={p.id} style={{ margin: 0, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                  <a href={`/api/admin/cleaning/photo/${p.id}`} target="_blank" rel="noreferrer">
                    <img src={`/api/admin/cleaning/photo/${p.id}`} alt={p.caption || ''} loading="lazy" style={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }} />
                  </a>
                  <figcaption style={{ padding: '6px 8px', fontSize: '.8rem' }}>
                    <strong>{PHOTO_CAT_LABEL[p.category] || p.category}</strong>
                    {p.caption && <div className="ctl-pre">{p.caption}</div>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        {comments.length > 0 && (
          <div className="crv-history">
            <div className="crv-history-title">{tx('clnT.63')}</div>
            {comments.map((c) => (
              <div key={c.id} className={`crv-item k-${c.kind}`}>
                <div className="crv-item-head">
                  <span>{KIND_LABEL[c.kind] || c.kind}</span>
                  <span className="crv-meta">{c.author_name || (c.author_type === 'admin' ? tx('clnT.64') : tx('clnT.65'))} {tx('clnT.66')} {jstTime(c.created_at)} {c.created_at ? dateLabel(new Date(new Date(c.created_at).getTime() + 9 * 3600e3).toISOString().slice(0, 10)) : ''}</span>
                </div>
                {c.body && <div className="ctl-pre">{c.body}</div>}
              </div>
            ))}
          </div>
        )}

        <label className="clm-label">{tx('clnT.67')}<strong>{tx('clnT.68')}</strong>{tx('clnT.69')}
          <textarea className="clm-input" rows={3} value={text} onChange={(e) => { setText(e.target.value); setErr(''); }}
            placeholder={canReview ? tx('clnT.70') : tx('clnT.71')} />
        </label>
        {err && <p className="cln-msg cln-msg-error">⚠ {err}</p>}
        {!canReview && <p className="clm-note">{tx('clnT.72')}</p>}

        {confirmReject ? (
          <div className="crv-confirm">
            <p><strong>{tx('clnT.73')}</strong><br />{tx('clnT.74')}</p>
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setConfirmReject(false)}>{tx('clnT.75')}</button>
              <button type="button" className="cln-btn crv-btn-reject" disabled={busy} onClick={() => void send('reject')}>{busy ? tx('clnT.76') : tx('clnT.77')}</button>
            </div>
          </div>
        ) : (
          <div className="crv-actions">
            <span className="crv-spacer" />
            <button type="button" className="cln-btn cln-btn-ghost" disabled={busy || !text.trim()} onClick={() => void send('comment')}>{tx('clnT.79')}</button>
            {canReview && (
              <>
                <button type="button" className="cln-btn crv-btn-reject-outline" disabled={busy}
                  onClick={() => { if (!text.trim()) { setErr(tx('clnT.50')); return; } setConfirmReject(true); }}>{tx('clnT.80')}</button>
                <button type="button" className="cln-btn crv-btn-approve" disabled={busy || task.review_status === 'approved'} onClick={() => void send('approve')}>
                  {task.review_status === 'approved' ? tx('clnT.81') : tx('clnT.47')}
                </button>
              </>
            )}
            <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={onClose}>{tx('clnT.82')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

